import { useCallback, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { auth, db } from "../../../app/firebase";
import { useDispatch } from "react-redux";
import { fetchEventTypes } from "../../../features/eventTypes/eventTypesSlice";
import AdminEventTypes from "./adminEventTypes";

function mapDocToItem(d) {
  const data = d.data() || {};
  return {
    id: d.id,
    key: data.key ?? "",
    category: data.category ?? "other",
    order: typeof data.order === "number" ? data.order : 0,
    enabledByDefault:
      typeof data.enabledByDefault === "boolean" ? data.enabledByDefault : true,
    iconUrl: data.iconUrl ?? "",
    labels: data.labels && typeof data.labels === "object" ? data.labels : {},
    supportsMedication: !!data.supportsMedication,
  };
}

export default function AdminEventTypesContainer() {
  const dispatch = useDispatch();
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadList = useCallback(async () => {
    if (!db) {
      setError("Firestore is not configured");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(collection(db, "eventTypes"));
      const list = [];
      snap.forEach((d) => list.push(mapDocToItem(d)));
      list.sort((a, b) => {
        if (a.category !== b.category) {
          return String(a.category).localeCompare(String(b.category));
        }
        if (a.order !== b.order) return a.order - b.order;
        return String(a.key).localeCompare(String(b.key));
      });
      setItems(list);
    } catch (e) {
      setError(e.message || "Failed to load event types");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCheckingAdmin(true);
      setIsAdmin(false);
      if (!user) {
        setCheckingAdmin(false);
        return;
      }
      try {
        const token = await user.getIdTokenResult(true);
        setIsAdmin(!!token.claims.admin);
      } finally {
        setCheckingAdmin(false);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (isAdmin) loadList();
  }, [isAdmin, loadList]);

  const handleSave = async (payload) => {
    if (!db) return { ok: false, error: "Firestore is not configured" };
    setSaving(true);
    setError(null);
    try {
      const labels = {};
      ["en", "ru", "sr"].forEach((lng) => {
        const v = (payload.labels?.[lng] || "").trim();
        if (v) labels[lng] = v;
      });

      const data = {
        key: payload.key.trim(),
        category: payload.category || "other",
        order: Number(payload.order) || 0,
        enabledByDefault: !!payload.enabledByDefault,
        iconUrl: (payload.iconUrl || "").trim() || null,
        labels,
        supportsMedication: !!payload.supportsMedication,
        updatedAt: serverTimestamp(),
      };

      if (!data.key) {
        return { ok: false, error: "Field «key» is required" };
      }
      if (Object.keys(labels).length === 0) {
        return { ok: false, error: "Add at least one label (en / ru / sr)" };
      }

      if (payload.editingId) {
        await updateDoc(doc(db, "eventTypes", payload.editingId), data);
      } else if (payload.newDocId && payload.newDocId.trim()) {
        const id = payload.newDocId.trim();
        if (!/^[a-zA-Z0-9_-]+$/.test(id)) {
          return {
            ok: false,
            error:
              "Document ID: only letters, numbers, underscore and hyphen",
          };
        }
        await setDoc(doc(db, "eventTypes", id), {
          ...data,
          createdAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, "eventTypes"), {
          ...data,
          createdAt: serverTimestamp(),
        });
      }

      await loadList();
      dispatch(fetchEventTypes());
      return { ok: true };
    } catch (e) {
      const msg = e.message || "Save failed";
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!db || !id) return { ok: false, error: "Invalid id" };
    if (!window.confirm(`Delete event type document "${id}"?`)) {
      return { ok: false, cancelled: true };
    }
    setSaving(true);
    setError(null);
    try {
      await deleteDoc(doc(db, "eventTypes", id));
      await loadList();
      dispatch(fetchEventTypes());
      return { ok: true };
    } catch (e) {
      const msg = e.message || "Delete failed";
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setSaving(false);
    }
  };

  if (checkingAdmin) {
    return <div style={{ padding: 16 }}>Checking admin rights…</div>;
  }
  if (!isAdmin) {
    return <div style={{ padding: 16 }}>403 — Admin access only</div>;
  }

  return (
    <AdminEventTypes
      items={items}
      loading={loading}
      saving={saving}
      error={error}
      onRefresh={loadList}
      onSave={handleSave}
      onDelete={handleDelete}
    />
  );
}
